import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import useRowLock from '@hooks/useRowLock';
import {
  selectEntity,
  selectCallback,
} from '@redux/selectors/RowLockSelector';
import {
  setRowLockCallback,
} from '@redux/modules/rowLock';
import { isValidEntity } from '@utils/rowLock';
import Button from '@components/smartAdminUI/Button';

const RowLockPage = () => {
  const params = useParams(); // use for retrieving the parameters of the current route
  const dispatch = useDispatch();
  const reduxRowLockEntity = useSelector(selectEntity);
  const reduxRowLockCallback = useSelector(selectCallback);

  /* Build the rowLock entity based on received route parameters. */
  const rowLockEntity = useMemo(() => ({ type: params.entityType, id: parseInt(params.entityId, 10) }), [params]);
  const {
    checked,
    lockingAuthorized,
    locked,
    lockedBy,
    lockedByMe,
    lastUpdatedOn,
    lastCheckedOn,
    checkStatus,
    createLock,
    removeLock,
    takeoverLock,
    discardChanges,
    LockMessage,
    LockOwnerMessage,
  } = useRowLock({ rowLockEntity });

  const entitiesAreValidAndMatch = useMemo(() => isValidEntity(rowLockEntity) && isEqual(rowLockEntity, reduxRowLockEntity), [rowLockEntity, reduxRowLockEntity]);

  /* Check if there is any callback action waiting to be fired. */
  useEffect(() => {
    if (entitiesAreValidAndMatch && reduxRowLockCallback && locked && lockingAuthorized) {
      reduxRowLockCallback();
      dispatch(setRowLockCallback(null));
    }
  }, [entitiesAreValidAndMatch, reduxRowLockCallback, locked, lockingAuthorized]);

  if (!isValidEntity(rowLockEntity)) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col">
            <p>Invalid entity</p>
          </div>
        </div>
      </div>
    );
  }

  if (!checked) return null;

  if (!locked) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col">
            <p>This entity is not currently locked.</p>

            {entitiesAreValidAndMatch && reduxRowLockCallback && (
              <Button name="btnCreateLock" variant="primary" onClick={() => createLock()}>
                <i className="fas fa-lock mr-2" aria-hidden="true" />
                Create lock and proceed
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col">
          {lockingAuthorized
            ? (
              <LockOwnerMessage
                onRemoveLock={removeLock}
              />
            )
            : (
              <LockMessage
                rowLockEntity={rowLockEntity}
                lockedBy={lockedBy}
                lockedByMe={lockedByMe}
                lastUpdatedOn={lastUpdatedOn}
                lastCheckedOn={lastCheckedOn}
                checkStatus={checkStatus}
                onTakeover={takeoverLock}
                onDiscard={discardChanges}
              />
            )}
        </div>
      </div>
    </div>
  );
};

export default RowLockPage;
